// runtime/api-effects.js
// Advanced effects, shading, and post-processing API for Nova64
import * as THREE from 'three';
import { logger } from './logger.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { FXAAShader } from 'three/examples/jsm/shaders/FXAAShader.js';
// OutputPass handles the renderer's configured tone mapping + sRGB output
// encoding. Required since THREE r152 — without it, post-processed colors
// don't reach the canvas the way carts expect. Notably: it also keeps the
// renderer's clear color path intact when bloom is enabled or removed, so
// nova64.scene.setClearColor(...) reaches the screen even with bloom on.
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';

// Chromatic Aberration shader
const ChromaticAberrationShader = {
  uniforms: {
    tDiffuse: { value: null },
    amount: { value: 0.002 },
  },
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform float amount;
    varying vec2 vUv;
    void main() {
      vec2 dir = vUv - 0.5;
      float dist = length(dir);
      vec2 offset = normalize(dir) * dist * amount;
      float r = texture2D(tDiffuse, vUv + offset).r;
      float g = texture2D(tDiffuse, vUv).g;
      float b = texture2D(tDiffuse, vUv - offset).b;
      gl_FragColor = vec4(r, g, b, 1.0);
    }
  `,
};

// Glitch/damage shader — scanline displacement, RGB split, block artifacts
const GlitchShader = {
  uniforms: {
    tDiffuse: { value: null },
    intensity: { value: 0.0 },
    time: { value: 0.0 },
  },
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform float intensity;
    uniform float time;
    varying vec2 vUv;

    float rand(vec2 co) {
      return fract(sin(dot(co, vec2(12.9898, 78.233))) * 43758.5453);
    }

    void main() {
      vec2 uv = vUv;

      // Scanline displacement — horizontal bands shift left/right
      float scanJitter = step(0.99 - intensity * 0.3, rand(vec2(time * 1.3, floor(uv.y * 40.0))))
                        * (rand(vec2(time, floor(uv.y * 40.0))) - 0.5) * intensity * 0.15;
      uv.x += scanJitter;

      // Block glitch — large rectangular regions shift
      float blockY = floor(uv.y * 8.0);
      float blockShift = step(0.97 - intensity * 0.15, rand(vec2(blockY, time * 0.7)))
                        * (rand(vec2(blockY + 1.0, time)) - 0.5) * intensity * 0.1;
      uv.x += blockShift;

      // RGB channel split
      float rgbShift = intensity * 0.012;
      float r = texture2D(tDiffuse, vec2(uv.x + rgbShift, uv.y + rgbShift * 0.5)).r;
      float g = texture2D(tDiffuse, uv).g;
      float b = texture2D(tDiffuse, vec2(uv.x - rgbShift, uv.y - rgbShift * 0.3)).b;

      // Color corruption — random bright pixels
      float noise = step(0.995 - intensity * 0.05, rand(uv + time)) * intensity;

      gl_FragColor = vec4(r + noise, g, b + noise * 0.5, 1.0);
    }
  `,
};

// Vignette shader
const VignetteShader = {
  uniforms: {
    tDiffuse: { value: null },
    darkness: { value: 1.5 },
    offset: { value: 0.95 },
  },
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform float darkness;
    uniform float offset;
    varying vec2 vUv;
    void main() {
      vec4 texel = texture2D(tDiffuse, vUv);
      vec2 uv = (vUv - vec2(0.5)) * vec2(offset);
      float vignette = 1.0 - dot(uv, uv) * darkness;
      gl_FragColor = vec4(texel.rgb * clamp(vignette, 0.0, 1.0), texel.a);
    }
  `,
};

export function effectsApi(gpu) {
  if (!gpu.getScene || !gpu.getCamera || !gpu.getRenderer) {
    return { exposeTo: () => {} };
  }

  const scene = gpu.getScene();
  const camera = gpu.getCamera();
  const renderer = gpu.getRenderer();

  // Check if this is Babylon.js backend - if so, delegate to gpu's built-in effects
  // Babylon.js Engine has 'scenes' array and 'getRenderingCanvas' method
  // Three.js WebGLRenderer has 'domElement' property
  const isBabylon =
    !renderer ||
    renderer.scenes !== undefined ||
    typeof renderer.getRenderingCanvas === 'function' ||
    renderer.constructor?.name === 'Engine' ||
    renderer.constructor?.name?.includes?.('Engine') ||
    !renderer.domElement;

  if (isBabylon) {
    // Babylon backend has its own effects implementation in effects.js
    // Return a wrapper that delegates to the gpu's effect functions
    return {
      exposeTo(target) {
        // These will be provided by gpu.exposeTo() from GpuBabylon's effects module
        // We just ensure they're available in the target namespace
        const babylonEffects = [
          'enableBloom',
          'disableBloom',
          'setBloomStrength',
          'setBloomRadius',
          'setBloomThreshold',
          'enableFXAA',
          'disableFXAA',
          'enableChromaticAberration',
          'disableChromaticAberration',
          'enableVignette',
          'disableVignette',
          'enableGlitch',
          'disableGlitch',
          'setGlitchIntensity',
          'enableRetroEffects',
          'disableRetroEffects',
          'isEffectsEnabled',
          'enableSharpen',
          'disableSharpen',
          'enableGrain',
          'disableGrain',
          // Custom shader materials (needed by Wizardry, etc.)
          'createShaderMaterial',
          'updateShaderUniform',
        ];

        // Only copy functions that exist on gpu and aren't already on target
        for (const key of babylonEffects) {
          if (typeof gpu[key] === 'function' && !(key in target)) {
            target[key] = gpu[key].bind(gpu);
          }
        }

        // Provide a no-op renderEffects for compatibility
        if (!target.renderEffects) {
          target.renderEffects = () => {
            // Babylon handles effects in its own render loop
          };
        }
      },

      // Babylon handles effect updates via gpu.updateEffects() called in its render loop
      update(deltaTime) {
        if (typeof gpu.updateEffects === 'function') {
          gpu.updateEffects(deltaTime);
        }
      },

      // Babylon handles rendering via its own scene.render()
      render() {
        // No-op: Babylon effects are applied in the DefaultRenderingPipeline
      },
    };
  }

  // === THREE.JS POST-PROCESSING IMPLEMENTATION ===

  // Post-processing composer
  let composer = null;
  let renderPass = null;
  let bloomPass = null;
  let fxaaPass = null;
  let chromaticAberrationPass = null;
  let vignettePass = null;
  let glitchPass = null;
  // OutputPass always lives at the END of the composer's pass list. This
  // keeps the EffectComposer's terminal pass renderToScreen=true at all
  // times — so addPass(bloom)/removePass(bloom) etc. never strip the
  // chain's screen output and `nova64.scene.setClearColor(...)` always
  // reaches the canvas. See addEffectPass() below for the helper that
  // inserts new effect passes BEFORE outputPass to preserve this.
  let outputPass = null;

  // Effect states
  let effectsEnabled = false;
  // effectsBypassed is shared on globalThis so HMR-reloaded api-effects
  // modules don't lose track of the bypass state between instances. The
  // closure-scoped pattern broke for indie-odyssey combat because the
  // setEffectsBypass call landed on a different module instance than the
  // one renderEffects was checking — the user's combat stayed dark.
  const BYPASS_KEY = '__nova64_fx_effects_bypassed__';
  if (typeof globalThis[BYPASS_KEY] !== 'boolean') globalThis[BYPASS_KEY] = false;
  let glitchTime = 0;

  // Custom shader materials
  const customShaders = new Map();

  // Initialize post-processing
  function initPostProcessing() {
    if (composer) return; // Already initialized

    composer = new EffectComposer(renderer);

    // Base render pass
    renderPass = new RenderPass(scene, camera);
    composer.addPass(renderPass);

    // Terminal output pass — handles tone mapping + sRGB output and keeps
    // the composer's screen-write path intact regardless of which effect
    // passes are added/removed between RenderPass and this one.
    outputPass = new OutputPass();
    composer.addPass(outputPass);

    effectsEnabled = true;
  }

  // Insert a new effect pass just BEFORE the terminal OutputPass so the
  // OutputPass always remains last (and renderToScreen=true on it stays
  // intact). Use this in place of composer.addPass() for any effect that
  // should contribute to the chain.
  function addEffectPass(pass) {
    if (!composer || !pass) return;
    if (outputPass && composer.passes.includes(outputPass)) {
      const idx = composer.passes.indexOf(outputPass);
      composer.insertPass(pass, idx);
    } else {
      composer.addPass(pass);
    }
  }

  // === BLOOM EFFECTS ===
  // Accepts EITHER positional args `enableBloom(strength, radius, threshold)`
  // OR an options object `enableBloom({ strength, radius, threshold })` —
  // existing carts (indie-odyssey, wizardry-3d, …) use the object form.
  function enableBloom(strengthOrOptions = 1.0, radius = 0.5, threshold = 0.6) {
    let strength = strengthOrOptions;
    if (strengthOrOptions && typeof strengthOrOptions === 'object') {
      strength = strengthOrOptions.strength ?? 1.0;
      radius = strengthOrOptions.radius ?? radius;
      threshold = strengthOrOptions.threshold ?? threshold;
    } else if (typeof strengthOrOptions !== 'number') {
      strength = 1.0;
    }

    initPostProcessing();

    // Return early if post-processing not supported (e.g., Babylon.js backend)
    if (!composer) {
      logger.warn('⚠️ Bloom effect not available with current backend');
      return false;
    }

    if (bloomPass) {
      composer.removePass(bloomPass);
    }

    bloomPass = new UnrealBloomPass(
      new THREE.Vector2(window.innerWidth, window.innerHeight),
      strength,
      radius,
      threshold
    );

    // Insert before OutputPass so output stays terminal.
    addEffectPass(bloomPass);

    return true;
  }

  function disableBloom() {
    if (bloomPass && composer) {
      composer.removePass(bloomPass);
      bloomPass = null;
    }
  }

  function setBloomStrength(strength) {
    if (bloomPass) {
      bloomPass.strength = strength;
    }
  }

  function setBloomRadius(radius) {
    if (bloomPass) {
      bloomPass.radius = radius;
    }
  }

  function setBloomThreshold(threshold) {
    if (bloomPass) {
      bloomPass.threshold = threshold;
    }
  }

  // === ANTI-ALIASING ===
  function enableFXAA() {
    initPostProcessing();

    // Return early if post-processing not supported
    if (!composer) {
      logger.warn('⚠️ FXAA not available with current backend');
      return false;
    }

    if (fxaaPass) return;

    fxaaPass = new ShaderPass(FXAAShader);
    const pixelRatio = renderer.getPixelRatio();
    fxaaPass.material.uniforms['resolution'].value.x = 1 / (window.innerWidth * pixelRatio);
    fxaaPass.material.uniforms['resolution'].value.y = 1 / (window.innerHeight * pixelRatio);

    addEffectPass(fxaaPass);
    return true;
  }

  function disableFXAA() {
    if (fxaaPass && composer) {
      composer.removePass(fxaaPass);
      fxaaPass = null;
    }
  }

  // === CHROMATIC ABERRATION ===
  function enableChromaticAberration(amount = 0.002) {
    initPostProcessing();

    // Return early if post-processing not supported
    if (!composer) {
      logger.warn('⚠️ Chromatic aberration not available with current backend');
      return false;
    }

    if (chromaticAberrationPass) {
      chromaticAberrationPass.uniforms['amount'].value = amount;
      return true;
    }
    chromaticAberrationPass = new ShaderPass(ChromaticAberrationShader);
    chromaticAberrationPass.uniforms['amount'].value = amount;
    addEffectPass(chromaticAberrationPass);
    return true;
  }

  function disableChromaticAberration() {
    if (chromaticAberrationPass && composer) {
      composer.removePass(chromaticAberrationPass);
      chromaticAberrationPass = null;
    }
  }

  // === VIGNETTE ===
  function enableVignette(darkness = 1.0, offset = 0.9) {
    initPostProcessing();

    // Return early if post-processing not supported
    if (!composer) {
      logger.warn('⚠️ Vignette effect not available with current backend');
      return false;
    }

    if (vignettePass) {
      vignettePass.uniforms['darkness'].value = darkness;
      vignettePass.uniforms['offset'].value = offset;
      return true;
    }
    vignettePass = new ShaderPass(VignetteShader);
    vignettePass.uniforms['darkness'].value = darkness;
    vignettePass.uniforms['offset'].value = offset;
    addEffectPass(vignettePass);
    return true;
  }

  function disableVignette() {
    if (vignettePass && composer) {
      composer.removePass(vignettePass);
      vignettePass = null;
    }
  }

  // === GLITCH EFFECT ===
  function enableGlitch(intensity = 0.5) {
    initPostProcessing();

    // Return early if post-processing not supported
    if (!composer) {
      logger.warn('⚠️ Glitch effect not available with current backend');
      return false;
    }

    if (glitchPass) {
      glitchPass.uniforms['intensity'].value = Math.max(0, Math.min(1, intensity));
      return true;
    }
    glitchPass = new ShaderPass(GlitchShader);
    glitchPass.uniforms['intensity'].value = Math.max(0, Math.min(1, intensity));
    addEffectPass(glitchPass);
    return true;
  }

  function disableGlitch() {
    if (glitchPass && composer) {
      composer.removePass(glitchPass);
      glitchPass = null;
    }
  }

  function setGlitchIntensity(intensity) {
    if (glitchPass) {
      glitchPass.uniforms['intensity'].value = Math.max(0, Math.min(1, intensity));
    }
  }

  // Convenience: fire a one-shot glitch BURST that starts at `intensity` and
  // decays to zero over `duration` seconds, then removes the pass. Self-driven
  // via rAF so carts get juicy damage/signal glitches in a single call:
  // `nova64.fx.glitchBurst(0.7, 0.3)`. Great for hits, scene stings, interference.
  let glitchBurstRAF = null;
  function glitchBurst(intensity = 0.6, duration = 0.3) {
    const peak = Math.max(0, Math.min(1, intensity));
    if (!enableGlitch(peak)) return false;
    const nowFn = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const raf = cb =>
      typeof requestAnimationFrame === 'function'
        ? requestAnimationFrame(cb)
        : setTimeout(() => cb(nowFn()), 16);
    const ms = Math.max(1, duration * 1000);
    const start = nowFn();
    if (glitchBurstRAF != null && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(glitchBurstRAF);
    }
    const tick = () => {
      const t = (nowFn() - start) / ms;
      if (t >= 1) {
        disableGlitch();
        glitchBurstRAF = null;
        return;
      }
      setGlitchIntensity(peak * (1 - t));
      glitchBurstRAF = raf(tick);
    };
    glitchBurstRAF = raf(tick);
    return true;
  }

  // === CUSTOM SHADERS ===

  // Holographic shader
  const holographicShader = {
    uniforms: {
      time: { value: 0 },
      color: { value: new THREE.Color(0x00ffff) },
      scanlineSpeed: { value: 2.0 },
      glitchAmount: { value: 0.1 },
      opacity: { value: 0.8 },
    },
    vertexShader: `
      varying vec2 vUv;
      varying vec3 vPosition;
      
      void main() {
        vUv = uv;
        vPosition = position;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform float time;
      uniform vec3 color;
      uniform float scanlineSpeed;
      uniform float glitchAmount;
      uniform float opacity;
      
      varying vec2 vUv;
      varying vec3 vPosition;
      
      void main() {
        // Scanlines
        float scanline = sin(vUv.y * 100.0 + time * scanlineSpeed) * 0.5 + 0.5;
        
        // Glitch effect
        float glitch = step(0.95, sin(time * 10.0 + vUv.y * 50.0)) * glitchAmount;
        
        // Edge glow
        float edge = 1.0 - abs(vUv.x - 0.5) * 2.0;
        edge = pow(edge, 3.0);
        
        // Combine effects
        vec3 finalColor = color * (scanline * 0.5 + 0.5) + vec3(edge * 0.3);
        finalColor += vec3(glitch);
        
        gl_FragColor = vec4(finalColor, opacity);
      }
    `,
  };

  // Energy shield shader
  const shieldShader = {
    uniforms: {
      time: { value: 0 },
      hitPosition: { value: new THREE.Vector3(0, 0, 0) },
      hitStrength: { value: 0 },
      color: { value: new THREE.Color(0x00ffff) },
      opacity: { value: 0.6 },
    },
    vertexShader: `
      varying vec2 vUv;
      varying vec3 vNormal;
      varying vec3 vPosition;
      
      void main() {
        vUv = uv;
        vNormal = normalize(normalMatrix * normal);
        vPosition = (modelMatrix * vec4(position, 1.0)).xyz;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform float time;
      uniform vec3 hitPosition;
      uniform float hitStrength;
      uniform vec3 color;
      uniform float opacity;
      
      varying vec2 vUv;
      varying vec3 vNormal;
      varying vec3 vPosition;
      
      void main() {
        // Fresnel effect
        vec3 viewDirection = normalize(cameraPosition - vPosition);
        float fresnel = pow(1.0 - dot(viewDirection, vNormal), 3.0);
        
        // Hexagon pattern
        vec2 hexUv = vUv * 20.0;
        float hexPattern = abs(sin(hexUv.x * 3.14159) * sin(hexUv.y * 3.14159));
        hexPattern = step(0.5, hexPattern);
        
        // Hit ripple
        float distToHit = length(vPosition - hitPosition);
        float ripple = sin(distToHit * 5.0 - time * 10.0) * 0.5 + 0.5;
        ripple *= smoothstep(2.0, 0.0, distToHit) * hitStrength;
        
        // Pulsing energy
        float pulse = sin(time * 2.0) * 0.2 + 0.8;
        
        // Combine effects
        vec3 finalColor = color * (fresnel + hexPattern * 0.3 + ripple) * pulse;
        float finalOpacity = opacity * (fresnel * 0.5 + 0.5 + ripple);
        
        gl_FragColor = vec4(finalColor, finalOpacity);
      }
    `,
  };

  // Water/liquid shader
  const waterShader = {
    uniforms: {
      time: { value: 0 },
      color: { value: new THREE.Color(0x0088ff) },
      waveSpeed: { value: 1.0 },
      waveHeight: { value: 0.5 },
      transparency: { value: 0.7 },
    },
    vertexShader: `
      uniform float time;
      uniform float waveSpeed;
      uniform float waveHeight;
      
      varying vec2 vUv;
      varying vec3 vNormal;
      varying float vElevation;
      
      void main() {
        vUv = uv;
        vNormal = normal;
        
        // Wave displacement
        vec3 pos = position;
        float wave1 = sin(pos.x * 2.0 + time * waveSpeed) * waveHeight;
        float wave2 = sin(pos.z * 3.0 + time * waveSpeed * 1.5) * waveHeight * 0.5;
        pos.y += wave1 + wave2;
        vElevation = wave1 + wave2;
        
        gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
      }
    `,
    fragmentShader: `
      uniform float time;
      uniform vec3 color;
      uniform float transparency;
      
      varying vec2 vUv;
      varying vec3 vNormal;
      varying float vElevation;
      
      void main() {
        // Foam on wave peaks
        float foam = smoothstep(0.3, 0.5, vElevation);
        
        // Caustics pattern
        vec2 causticsUv = vUv * 10.0 + time * 0.1;
        float caustics = abs(sin(causticsUv.x * 3.14159) * sin(causticsUv.y * 3.14159));
        
        // Depth fade
        float depth = vUv.y;
        vec3 deepColor = color * 0.5;
        vec3 shallowColor = color * 1.5;
        vec3 finalColor = mix(deepColor, shallowColor, depth);
        
        // Add foam and caustics
        finalColor += vec3(foam * 0.5);
        finalColor += vec3(caustics * 0.2);
        
        gl_FragColor = vec4(finalColor, transparency);
      }
    `,
  };

  // Fire/plasma shader
  const fireShader = {
    uniforms: {
      time: { value: 0 },
      color1: { value: new THREE.Color(0xff4400) },
      color2: { value: new THREE.Color(0xffff00) },
      intensity: { value: 1.0 },
      speed: { value: 2.0 },
    },
    vertexShader: `
      varying vec2 vUv;
      varying vec3 vPosition;
      
      void main() {
        vUv = uv;
        vPosition = position;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform float time;
      uniform vec3 color1;
      uniform vec3 color2;
      uniform float intensity;
      uniform float speed;
      
      varying vec2 vUv;
      varying vec3 vPosition;
      
      // Noise function
      float random(vec2 st) {
        return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123);
      }
      
      float noise(vec2 st) {
        vec2 i = floor(st);
        vec2 f = fract(st);
        float a = random(i);
        float b = random(i + vec2(1.0, 0.0));
        float c = random(i + vec2(0.0, 1.0));
        float d = random(i + vec2(1.0, 1.0));
        vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(a, b, u.x) + (c - a)* u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
      }
      
      void main() {
        vec2 uv = vUv;
        
        // Multiple layers of noise for fire effect
        float n1 = noise(uv * 5.0 + time * speed);
        float n2 = noise(uv * 10.0 + time * speed * 1.5);
        float n3 = noise(uv * 20.0 + time * speed * 2.0);
        
        // Combine noise layers
        float firePattern = n1 * 0.5 + n2 * 0.3 + n3 * 0.2;
        
        // Vertical gradient (fire rises)
        float gradient = 1.0 - uv.y;
        firePattern *= gradient;
        
        // Color mixing
        vec3 fireColor = mix(color1, color2, firePattern);
        
        // Intensity and flickering
        float flicker = sin(time * 10.0) * 0.1 + 0.9;
        fireColor *= intensity * flicker;
        
        // Alpha based on pattern
        float alpha = firePattern * intensity;
        
        gl_FragColor = vec4(fireColor, alpha);
      }
    `,
  };

  // Detect if running on Babylon.js backend
  function isBabylonBackend() {
    return (
      !renderer || renderer.scenes || renderer.constructor.name === 'Engine' || !renderer.domElement
    );
  }

  // Create material with custom shader
  function createShaderMaterial(shaderName, customUniforms = {}) {
    // Delegate to Babylon.js backend if applicable
    if (isBabylonBackend()) {
      // The Babylon backend exposes createShaderMaterial on the gpu object
      if (typeof gpu.createShaderMaterial === 'function') {
        return gpu.createShaderMaterial(shaderName, customUniforms);
      }
      logger.warn(`Shader material '${shaderName}' not available with Babylon.js backend`);
      return null;
    }

    let shader;

    switch (shaderName) {
      case 'holographic':
        shader = holographicShader;
        break;
      case 'shield':
        shader = shieldShader;
        break;
      case 'water':
        shader = waterShader;
        break;
      case 'fire':
        shader = fireShader;
        break;
      default:
        logger.warn(`Unknown shader: ${shaderName}`);
        return null;
    }

    // Merge custom uniforms with shader uniforms
    const uniforms = { ...shader.uniforms };
    for (const key in customUniforms) {
      if (uniforms[key]) {
        uniforms[key].value = customUniforms[key];
      }
    }

    const material = new THREE.ShaderMaterial({
      uniforms: uniforms,
      vertexShader: shader.vertexShader,
      fragmentShader: shader.fragmentShader,
      transparent: true,
      side: THREE.DoubleSide,
    });

    const id = `shader_${Date.now()}_${Math.random()}`;
    customShaders.set(id, material);

    return { id, material };
  }

  // Update shader uniforms
  function updateShaderUniform(shaderId, uniformName, value) {
    // Delegate to Babylon.js backend if applicable
    if (isBabylonBackend()) {
      if (typeof gpu.updateShaderUniform === 'function') {
        return gpu.updateShaderUniform(shaderId, uniformName, value);
      }
      return false;
    }

    const material = customShaders.get(shaderId);
    if (material && material.uniforms[uniformName]) {
      material.uniforms[uniformName].value = value;
      return true;
    }
    return false;
  }

  // Update all shader time uniforms
  function updateShaderTime(deltaTime) {
    customShaders.forEach(material => {
      if (material.uniforms.time) {
        material.uniforms.time.value += deltaTime;
      }
    });
  }

  // NOTE: Particle system is provided by api-3d/particles.js (GPU InstancedMesh).
  // Legacy Points-based particle functions were removed to avoid overwriting the 3D API.

  // === CONVENIENCE: enable a full retro N64/PS1 post-processing stack in one call ===
  /**
   * enableRetroEffects(opts)
   * One-call setup for bloom + FXAA + vignette + optional chromatic aberration.
   * opts: {
   *   bloom:     { strength, radius, threshold }   | false to skip  (default: {1.5, 0.4, 0.1})
   *   fxaa:      true | false                                        (default: true)
   *   vignette:  { darkness, offset }               | false to skip  (default: {1.3, 0.9})
   *   chromatic: number (amount) | false to skip                     (default: false)
   *   pixelation: number (factor) | false to skip                    (default: 1)
   *   dithering: boolean                                              (default: true)
   * }
   * Call with no args for sensible defaults that match Star Fox / Crystal Cathedral look.
   */
  function enableRetroEffects(opts = {}) {
    // Pixelation — delegated to gpu (api-3d)
    const pixelFactor = opts.pixelation !== undefined ? opts.pixelation : 1;
    const enablePixelation = globalThis.enablePixelation ?? globalThis.nova64?.fx?.enablePixelation;
    if (pixelFactor !== false && typeof enablePixelation === 'function') {
      enablePixelation(pixelFactor);
    }

    // Dithering — delegated to gpu (api-3d)
    const dither = opts.dithering !== undefined ? opts.dithering : true;
    const enableDithering = globalThis.enableDithering ?? globalThis.nova64?.fx?.enableDithering;
    if (dither !== false && typeof enableDithering === 'function') {
      enableDithering(dither);
    }

    // Bloom
    const bloom = opts.bloom !== undefined ? opts.bloom : {};
    if (bloom !== false) {
      const b = typeof bloom === 'object' ? bloom : {};
      enableBloom(b.strength ?? 1.0, b.radius ?? 0.4, b.threshold ?? 0.6);
    }

    // FXAA
    const fxaa = opts.fxaa !== undefined ? opts.fxaa : true;
    if (fxaa !== false) {
      enableFXAA();
    }

    // Vignette
    const vig = opts.vignette !== undefined ? opts.vignette : {};
    if (vig !== false) {
      const v = typeof vig === 'object' ? vig : {};
      enableVignette(v.darkness ?? 1.3, v.offset ?? 0.9);
    }

    // Chromatic aberration (off by default — slight colour fringing)
    const chrom = opts.chromatic !== undefined ? opts.chromatic : false;
    if (chrom !== false) {
      enableChromaticAberration(typeof chrom === 'number' ? chrom : 0.002);
    }

    return true;
  }

  /**
   * disableRetroEffects()
   * Tear down everything enableRetroEffects() set up.
   */
  function disableRetroEffects() {
    disableBloom();
    disableFXAA();
    disableVignette();
    disableChromaticAberration();
    if (typeof globalThis.enablePixelation === 'function') globalThis.enablePixelation(0);
    else if (typeof globalThis.nova64?.fx?.enablePixelation === 'function')
      globalThis.nova64.fx.enablePixelation(0);
    if (typeof globalThis.enableDithering === 'function') globalThis.enableDithering(false);
    else if (typeof globalThis.nova64?.fx?.enableDithering === 'function')
      globalThis.nova64.fx.enableDithering(false);
  }

  // === RENDERING ===
  let _renderEffectsLogCount = 0;
  function renderEffects() {
    // First-frame diagnostic so we can see which path the user's browser
    // actually takes. Logs once per ~120 frames (2s @ 60fps) until cleared.
    if (_renderEffectsLogCount++ % 120 === 0) {
      console.log(
        '[api-fx] renderEffects: effectsEnabled=' +
          effectsEnabled +
          ' composer=' +
          !!composer +
          ' bypassed=' +
          !!globalThis[BYPASS_KEY] +
          ' → using ' +
          (effectsEnabled && composer && !globalThis[BYPASS_KEY] ? 'COMPOSER' : 'DIRECT')
      );
    }
    if (effectsEnabled && composer && !globalThis[BYPASS_KEY]) {
      composer.render();
    } else {
      // Composer leaves the renderer pointing at an internal RenderTarget
      // after running, so a direct renderer.render() would draw INTO that
      // target instead of the screen — that's why bypass mode looked
      // black/transparent for indie-odyssey combat. Reset to null (screen)
      // and force-clear with the renderer's clear color (composer's last
      // pass may have left clearColor/clearAlpha overridden internally)
      // before drawing the scene.
      const wasAutoClear = renderer.autoClear;
      renderer.setRenderTarget(null);
      renderer.autoClear = true;
      // Explicit clear to repaint the canvas with the renderer's clear
      // colour — guards against the composer leaving its internal
      // clearColor/clearAlpha values latched.
      renderer.clear(true, true, true);
      renderer.render(scene, camera);
      renderer.autoClear = wasAutoClear;
    }
  }

  // === EFFECTS BYPASS ===
  // Some cart phases (e.g. an Indie-Odyssey-style combat scene with a flat
  // procedural sky) need the renderer's clear color to reach the canvas
  // pixel-perfect, without any composer pass touching the alpha or color.
  // Bypass mode routes endFrame through renderer.render(scene, camera)
  // directly, skipping the EffectComposer. Toggle off when leaving the
  // bypassed phase to resume normal post-processing.
  function setEffectsBypass(bypass) {
    globalThis[BYPASS_KEY] = !!bypass;
    console.log(
      '[api-fx] setEffectsBypass(' +
        !!bypass +
        ') — globalThis[' +
        BYPASS_KEY +
        '] = ' +
        globalThis[BYPASS_KEY]
    );
  }
  function isEffectsBypassed() {
    return !!globalThis[BYPASS_KEY];
  }

  // Update effects (called every frame)
  function updateEffects(deltaTime) {
    updateShaderTime(deltaTime);
    // Animate glitch time uniform for randomness
    if (glitchPass) {
      glitchTime += deltaTime;
      glitchPass.uniforms['time'].value = glitchTime;
    }
  }

  // === EXPOSE API ===
  return {
    exposeTo(target) {
      Object.assign(target, {
        // Post-processing
        enableBloom: enableBloom,
        disableBloom: disableBloom,
        setBloomStrength: setBloomStrength,
        setBloomRadius: setBloomRadius,
        setBloomThreshold: setBloomThreshold,
        enableFXAA: enableFXAA,
        disableFXAA: disableFXAA,
        enableChromaticAberration: enableChromaticAberration,
        disableChromaticAberration: disableChromaticAberration,
        enableVignette: enableVignette,
        disableVignette: disableVignette,
        enableGlitch: enableGlitch,
        disableGlitch: disableGlitch,
        setGlitchIntensity: setGlitchIntensity,
        glitchBurst: glitchBurst,

        // Glow layer — Babylon-specific. On Three.js, UnrealBloom already
        // handles emissive glow well, so this is a graceful no-op that returns
        // false so carts can branch on the result if they care.
        enableGlow: () => false,
        disableGlow: () => {},

        // Effects bypass — temporarily skip the EffectComposer to get a
        // guaranteed-correct render through renderer.render(). Use this for
        // combat scenes / cutscenes where the canvas must show the renderer's
        // exact clear color and a composer pass would otherwise output
        // alpha=0 (UnrealBloomPass × OutputPass interaction).
        setEffectsBypass,
        isEffectsBypassed,

        // Convenience
        enableRetroEffects: enableRetroEffects,
        disableRetroEffects: disableRetroEffects,

        // Custom shaders
        createShaderMaterial: createShaderMaterial,
        updateShaderUniform: updateShaderUniform,

        // NOTE: Particle system is provided by api-3d/particles.js (GPU InstancedMesh).
        // The legacy Points-based particle functions here are kept internal only
        // to avoid overwriting the more capable 3D particle API.

        // Utility
        isEffectsEnabled: () => effectsEnabled,

        // Called by gpu-threejs.js endFrame() to apply the effect composer
        renderEffects: renderEffects,
      });
    },

    // Internal update called by main loop
    update(deltaTime) {
      updateEffects(deltaTime);
    },

    // Internal render called by main loop
    render() {
      renderEffects();
    },
  };
}
